
import { Record, Union } from "../fable_modules/fable-library-js.5.0.0/Types.js";
import { float64_type, record_type, list_type, bool_type, option_type, int32_type, class_type, union_type } from "../fable_modules/fable-library-js.5.0.0/Reflection.js";
import { length, collect, singleton, exists, tryLast, filter, pairwise, sort, toArray as toArray_1, sortBy, tail, head, reverse, empty, cons, fold, choose, tryFind, map as map_1, append, sortByDescending, max as max_2, isEmpty, sumBy, ofArray } from "../fable_modules/fable-library-js.5.0.0/List.js";
import { SensorId_$reflection } from "./PortWind.js";
import { op_Subtraction } from "../fable_modules/fable-library-js.5.0.0/DateOffset.js";
import { ticks, fromTicks, totalSeconds, fromSeconds, fromHours, fromMinutes } from "../fable_modules/fable-library-js.5.0.0/TimeSpan.js";
import { compareArrays, equals, compare, min, comparePrimitives, max } from "../fable_modules/fable-library-js.5.0.0/Util.js";
import { toArray, some, map, defaultArg } from "../fable_modules/fable-library-js.5.0.0/Option.js";
import { max as max_1 } from "../fable_modules/fable-library-js.5.0.0/Double.js";
import { compare as compare_1, op_Addition, toInt64_unchecked } from "../fable_modules/fable-library-js.5.0.0/BigInt.js";
import { equals as equals_1, compare as compare_2, getTicks } from "../fable_modules/fable-library-js.5.0.0/Date.js";
import { item } from "../fable_modules/fable-library-js.5.0.0/Array.js";
import { union, FSharpSet__Contains, isEmpty as isEmpty_1, singleton as singleton_1, empty as empty_1, ofList } from "../fable_modules/fable-library-js.5.0.0/Set.js";

/**
 * A sensor's measurements can come from more than one physical device - the wind unit, a tide
 * gauge, a wave radar - each its own stream publishing at its own pace.
 */
export class Stream extends Union {
    constructor(tag, fields) {
        super();
        this.tag = tag;
        this.fields = fields;
    }
    cases() {
        return ["Wind", "Waves", "Tide", "TidalFlow"];
    }
}

export function Stream_$reflection() {
    return union_type("StenaWeather.Domain.PortWindOutage.Stream", [], Stream, () => [[], [], [], []]);
}

export const StreamModule_all = ofArray([new Stream(0, []), new Stream(1, []), new Stream(2, []), new Stream(3, [])]);

export function StreamModule_label(_arg) {
    switch (_arg.tag) {
        case 1:
            return "Waves";
        case 2:
            return "Tide";
        case 3:
            return "Tidal flow";
        default:
            return "Wind";
    }
}

/**
 * Whether a measurement carries this stream. Grouped by device, so the parameters a device
 * sends together (the wind trio) are one stream. WindSpeedAverage is left out - it is derived
 * from WindSpeed, not a stream of its own.
 */
export function StreamModule_present(stream, w) {
    switch (stream.tag) {
        case 1:
            return w.WaveHeight != null;
        case 2:
            return w.TideHeight != null;
        case 3:
            return w.TidalFlowSpeed != null;
        default:
            if (((w.WindSpeed != null) ? true : (w.WindDirection != null)) ? true : (w.GustWindSpeed != null)) {
                return true;
            }
            else {
                return w.MaxGustWindSpeed != null;
            }
    }
}

export class IssueKind extends Union {
    constructor(tag, fields) {
        super();
        this.tag = tag;
        this.fields = fields;
    }
    cases() {
        return ["Gap", "Delay"];
    }
}

export function IssueKind_$reflection() {
    return union_type("StenaWeather.Domain.PortWindOutage.IssueKind", [], IssueKind, () => [[], []]);
}

export class Issue extends Record {
    constructor(SensorId, Kind, From, To, Points, MaxDelay, IsOngoing, Streams) {
        super();
        this.SensorId = SensorId;
        this.Kind = Kind;
        this.From = From;
        this.To = To;
        this.Points = (Points | 0);
        this.MaxDelay = MaxDelay;
        this.IsOngoing = IsOngoing;
        this.Streams = Streams;
    }
}

export function Issue_$reflection() {
    return record_type("StenaWeather.Domain.PortWindOutage.Issue", [], Issue, () => [["SensorId", SensorId_$reflection()], ["Kind", IssueKind_$reflection()], ["From", class_type("System.DateTimeOffset")], ["To", class_type("System.DateTimeOffset")], ["Points", int32_type], ["MaxDelay", option_type(class_type("System.TimeSpan"))], ["IsOngoing", bool_type], ["Streams", list_type(Stream_$reflection())]]);
}

export function Issue__get_Duration(this$) {
    return op_Subtraction(this$.To, this$.From);
}

/**
 * Both of these are read against the sensor's own pace rather than against a fixed duration: a
 * sensor reporting every quarter of an hour is neither missing nor behind at eleven minutes the
 * way a sensor reporting every minute is.
 */
export class Thresholds extends Record {
    constructor(GapIntervalFactor, GapWithoutInterval, GapCap, DelayIntervalFactor, DelayWithoutInterval, Minimum) {
        super();
        this.GapIntervalFactor = GapIntervalFactor;
        this.GapWithoutInterval = GapWithoutInterval;
        this.GapCap = GapCap;
        this.DelayIntervalFactor = DelayIntervalFactor;
        this.DelayWithoutInterval = DelayWithoutInterval;
        this.Minimum = Minimum;
    }
}

export function Thresholds_$reflection() {
    return record_type("StenaWeather.Domain.PortWindOutage.Thresholds", [], Thresholds, () => [["GapIntervalFactor", float64_type], ["GapWithoutInterval", class_type("System.TimeSpan")], ["GapCap", class_type("System.TimeSpan")], ["DelayIntervalFactor", float64_type], ["DelayWithoutInterval", class_type("System.TimeSpan")], ["Minimum", class_type("System.TimeSpan")]]);
}

export const ThresholdsModule_Default = new Thresholds(5, fromMinutes(20), fromHours(2), 5, fromMinutes(20), fromMinutes(10));

function ThresholdsModule_scaled(thresholds, factor, fallback, interval) {
    return max((x, y) => (comparePrimitives(x, y) | 0), thresholds.Minimum, defaultArg(map((i) => fromSeconds(totalSeconds(i) * factor), interval), fallback));
}

/**
 * What counts as a gap for a sensor reporting at `interval`.
 */
export function ThresholdsModule_gap(thresholds, interval) {
    return min((x, y) => (comparePrimitives(x, y) | 0), thresholds.GapCap, ThresholdsModule_scaled(thresholds, thresholds.GapIntervalFactor, thresholds.GapWithoutInterval, interval));
}

/**
 * What counts as delayed for a sensor reporting at `interval`.
 */
export function ThresholdsModule_delay(thresholds, interval) {
    return ThresholdsModule_scaled(thresholds, thresholds.DelayIntervalFactor, thresholds.DelayWithoutInterval, interval);
}

function share(window$, lost) {
    if (window$ <= 0) {
        return undefined;
    }
    else {
        return max_1(0, 1 - (totalSeconds(lost) / totalSeconds(window$)));
    }
}

function sumDurations(issues) {
    return fromTicks(sumBy((i) => ticks(Issue__get_Duration(i)), issues, {
        GetZero: () => (0n),
        Add: (x, y) => toInt64_unchecked(op_Addition(x, y)),
    }));
}

function tryMax(xs) {
    if (isEmpty(xs)) {
        return undefined;
    }
    else {
        return some(max_2(xs, {
            Compare: (x, y) => (compare(x, y) | 0),
        }));
    }
}

export class Report extends Record {
    constructor(SensorId, From, To, Points, TypicalInterval, GapThreshold, DelayThreshold, Gaps, Delays, LastMeasurement) {
        super();
        this.SensorId = SensorId;
        this.From = From;
        this.To = To;
        this.Points = (Points | 0);
        this.TypicalInterval = TypicalInterval;
        this.GapThreshold = GapThreshold;
        this.DelayThreshold = DelayThreshold;
        this.Gaps = Gaps;
        this.Delays = Delays;
        this.LastMeasurement = LastMeasurement;
    }
}

export function Report_$reflection() {
    return record_type("StenaWeather.Domain.PortWindOutage.Report", [], Report, () => [["SensorId", SensorId_$reflection()], ["From", class_type("System.DateTimeOffset")], ["To", class_type("System.DateTimeOffset")], ["Points", int32_type], ["TypicalInterval", option_type(class_type("System.TimeSpan"))], ["GapThreshold", class_type("System.TimeSpan")], ["DelayThreshold", class_type("System.TimeSpan")], ["Gaps", list_type(Issue_$reflection())], ["Delays", list_type(Issue_$reflection())], ["LastMeasurement", option_type(class_type("System.DateTimeOffset"))]]);
}

/**
 * Everything the report found, most recent first - what the issue table lists.
 */
export function Report__get_Issues(this$) {
    return sortByDescending((_arg) => getTicks(_arg.From), append(this$.Gaps, this$.Delays), {
        Compare: (x, y) => (compare_1(x, y) | 0),
    });
}

export function Report__get_MissingTime(this$) {
    return sumDurations(this$.Gaps);
}

/**
 * The longest the sensor has been silent. The gap running right now counts from its last
 * measurement, so this can reach further back than the window.
 */
export function Report__get_LongestGap(this$) {
    return tryMax(append(ofArray(toArray(Report__get_CurrentGap(this$))), map_1(Issue__get_Duration, this$.Gaps)));
}

/**
 * How long the sensor has been silent for, when it is silent right now. None when its last
 * measurement is recent enough that the window does not end in a gap. Measured from the
 * last measurement there is rather than from the start of the window, so a sensor that went
 * quiet three days ago reads as three days silent on a one day report.
 */
export function Report__get_CurrentGap(this$) {
    return map((gap) => {
        const matchValue = this$.LastMeasurement;
        let matchResult, t_1;
        if (matchValue != null) {
            if (compare_2(matchValue, gap.From) <= 0) {
                matchResult = 0;
                t_1 = matchValue;
            }
            else {
                matchResult = 1;
            }
        }
        else {
            matchResult = 1;
        }
        switch (matchResult) {
            case 0:
                return op_Subtraction(this$.To, t_1);
            default:
                return Issue__get_Duration(gap);
        }
    }, tryFind((_arg) => _arg.IsOngoing, this$.Gaps));
}

/**
 * True when the sensor is not reporting right now, as opposed to having had gaps earlier in
 * the window and recovered since.
 */
export function Report__get_IsOut(this$) {
    return Report__get_CurrentGap(this$) != null;
}

export function Report__get_DelayedPoints(this$) {
    return sumBy((_arg) => (_arg.Points | 0), this$.Delays, {
        GetZero: () => 0,
        Add: (x, y) => ((x + y) | 0),
    }) | 0;
}

/**
 * How long the sensor's measurements were arriving too late to be of use.
 */
export function Report__get_DelayedTime(this$) {
    return sumDurations(this$.Delays);
}

/**
 * Share of the window the sensor was delivering usable data in, 0.0 - 1.0. A delayed
 * interval counts against it the same as a gap: a measurement that arrives half an hour
 * after the time it describes was not there when it was needed. None for an empty window.
 */
export function Report__get_Availability(this$) {
    return share(op_Subtraction(this$.To, this$.From), Report__get_MissingTime(this$) + Report__get_DelayedTime(this$));
}

/**
 * Share of the window the sensor's measurements cover at all, however late they arrived,
 * 0.0 - 1.0. None for an empty window.
 */
export function Report__get_DataCoverage(this$) {
    return share(op_Subtraction(this$.To, this$.From), Report__get_MissingTime(this$));
}

export function Report__get_MaxDelay(this$) {
    return tryMax(choose((_arg) => _arg.MaxDelay, this$.Delays));
}

/**
 * What the report says about a sensor as a whole - the one line an operator acts on.
 */
export class Health extends Union {
    constructor(tag, fields) {
        super();
        this.tag = tag;
        this.fields = fields;
    }
    cases() {
        return ["Ok", "NoData", "Delayed", "PastOutage", "Outage"];
    }
}

export function Health_$reflection() {
    return union_type("StenaWeather.Domain.PortWindOutage.Health", [], Health, () => [[], [], [], [], []]);
}

/**
 * Worst first: being silent right now outranks having been silent, which outranks being
 * late, because that is the order someone looking at the list needs to act in.
 */
export function HealthModule_ofReport(report) {
    if (report.LastMeasurement == null) {
        return new Health(1, []);
    }
    else if (Report__get_IsOut(report)) {
        return new Health(4, []);
    }
    else if (!isEmpty(report.Gaps)) {
        return new Health(3, []);
    }
    else if (!isEmpty(report.Delays)) {
        return new Health(2, []);
    }
    else {
        return new Health(0, []);
    }
}

function span(w) {
    return [defaultArg(w.DateTimeMeanStart, w.DateTime), w.DateTime];
}

function segments(rangeStart, rangeEnd, intervals) {
    const patternInput = fold((tupledArg, tupledArg_1) => {
        const cursor = tupledArg[0];
        const acc = tupledArg[1];
        const s = tupledArg_1[1];
        const e = tupledArg_1[2];
        const acc_1 = (compare_2(s, cursor) > 0) ? cons([undefined, cursor, s], acc) : acc;
        const s_1 = max((x, y) => (compare_2(x, y) | 0), cursor, s);
        const acc_2 = (compare_2(e, s_1) > 0) ? cons([some(tupledArg_1[0]), s_1, e], acc_1) : acc_1;
        return [max((x_1, y_1) => (compare_2(x_1, y_1) | 0), cursor, e), acc_2];
    }, [rangeStart, empty()], intervals);
    const cursor_1 = patternInput[0];
    const acc_3 = patternInput[1];
    return reverse((compare_2(rangeEnd, cursor_1) > 0) ? cons([undefined, cursor_1, rangeEnd], acc_3) : acc_3);
}

function gapSpans(threshold, rangeStart, rangeEnd, spans) {
    return choose((_arg) => {
        let matchResult;
        if (_arg[0] == null) {
            if (op_Subtraction(_arg[2], _arg[1]) > threshold) {
                matchResult = 0;
            }
            else {
                matchResult = 1;
            }
        }
        else {
            matchResult = 1;
        }
        switch (matchResult) {
            case 0:
                return [_arg[1], _arg[2]];
            default:
                return undefined;
        }
    }, segments(rangeStart, rangeEnd, map_1((tupledArg) => [undefined, tupledArg[0], tupledArg[1]], spans)));
}

function delaySpans(threshold, points) {
    return reverse(fold((tupledArg, w) => {
        const runs = tupledArg[0];
        const matchValue = w.ReceivedDelay;
        let matchResult, d_1;
        if (matchValue != null) {
            if (matchValue > threshold) {
                matchResult = 0;
                d_1 = matchValue;
            }
            else {
                matchResult = 1;
            }
        }
        else {
            matchResult = 1;
        }
        switch (matchResult) {
            case 0: {
                const patternInput = span(w);
                const e = patternInput[1];
                let matchResult_1, maxDelay, n, rest, runStart;
                if (!isEmpty(runs)) {
                    if (tupledArg[1]) {
                        matchResult_1 = 0;
                        maxDelay = head(runs)[2];
                        n = head(runs)[3];
                        rest = tail(runs);
                        runStart = head(runs)[0];
                    }
                    else {
                        matchResult_1 = 1;
                    }
                }
                else {
                    matchResult_1 = 1;
                }
                switch (matchResult_1) {
                    case 0:
                        return [cons([runStart, e, max((x, y) => (comparePrimitives(x, y) | 0), maxDelay, d_1), n + 1], rest), true];
                    default:
                        return [cons([patternInput[0], e, d_1, 1], runs), true];
                }
            }
            default:
                return [runs, false];
        }
    }, [empty(), false], points)[0]);
}

function coalesceBy(mergePayload, intervals) {
    return reverse(fold((acc, tupledArg_1) => {
        const s_1 = tupledArg_1[0];
        const e = tupledArg_1[1];
        const p = tupledArg_1[2];
        let matchResult, pe_1, pp_1, ps_1, rest_1;
        if (!isEmpty(acc)) {
            if (compare_2(s_1, head(acc)[1]) < 0) {
                matchResult = 0;
                pe_1 = head(acc)[1];
                pp_1 = head(acc)[2];
                ps_1 = head(acc)[0];
                rest_1 = tail(acc);
            }
            else {
                matchResult = 1;
            }
        }
        else {
            matchResult = 1;
        }
        switch (matchResult) {
            case 0:
                return cons([ps_1, max((x_1, y_1) => (compare_2(x_1, y_1) | 0), pe_1, e), mergePayload(pp_1, p)], rest_1);
            default:
                return cons([s_1, e, p], acc);
        }
    }, empty(), sortBy((tupledArg) => getTicks(tupledArg[0]), intervals, {
        Compare: (x, y) => (compare_1(x, y) | 0),
    })));
}

function typicalInterval(points) {
    const diffs = toArray_1(sort(map_1((tupledArg) => ticks(op_Subtraction(tupledArg[1].DateTime, tupledArg[0].DateTime)), pairwise(points)), {
        Compare: (x, y) => (compare_1(x, y) | 0),
    }));
    if (diffs.length === 0) {
        return undefined;
    }
    else {
        return fromTicks(item(~~(diffs.length / 2), diffs));
    }
}

/**
 * The gaps and the delayed intervals of one sensor within the window. The weather may come in
 * any order. `lastKnown` is the sensor's newest measurement whenever that is known, which can be
 * older than the window and is the only thing that says how long a silent sensor has been
 * silent - the window itself only knows that nothing arrived inside it.
 */
export function analyse(thresholds, rangeStart, rangeEnd, sensorId, lastKnown, weather) {
    const points = sortBy((_arg) => getTicks(_arg.DateTime), filter((w) => {
        if (compare_2(w.DateTime, rangeStart) >= 0) {
            return compare_2(w.DateTime, rangeEnd) <= 0;
        }
        else {
            return false;
        }
    }, weather), {
        Compare: (x, y) => (compare_1(x, y) | 0),
    });
    const lastPointTime = map((_arg_1) => _arg_1.DateTime, tryLast(points));
    const mkIssue = (kind, from, to_, count, maxDelay, isOngoing, streams) => (new Issue(sensorId, kind, from, to_, count, maxDelay, isOngoing, streams));
    const produced = filter((st) => exists((w_1) => StreamModule_present(st, w_1), points), StreamModule_all);
    const producedSet = ofList(produced, {
        Compare: (x_1, y_1) => (compare(x_1, y_1) | 0),
    });
    const analyseGroup = (streams_1, groupPoints) => {
        let spans;
        const interval = typicalInterval(groupPoints);
        const gaps = map_1((tupledArg) => [tupledArg[0], tupledArg[1], streams_1], (spans = map_1(span, groupPoints), gapSpans(ThresholdsModule_gap(thresholds, interval), rangeStart, rangeEnd, spans)));
        const streamLast = map((_arg_2) => _arg_2.DateTime, tryLast(groupPoints));
        return [gaps, map_1((tupledArg_1) => {
            const e_1 = tupledArg_1[1];
            return [tupledArg_1[0], e_1, [tupledArg_1[2], tupledArg_1[3], equals(streamLast, e_1), streams_1]];
        }, delaySpans(ThresholdsModule_delay(thresholds, interval), groupPoints))];
    };
    const groups = isEmpty(produced) ? singleton(analyseGroup(empty_1({
        Compare: (x_2, y_2) => (compare(x_2, y_2) | 0),
    }), points)) : map_1((st_1) => analyseGroup(singleton_1(st_1, {
        Compare: (x_3, y_3) => (compare(x_3, y_3) | 0),
    }), filter((w_3) => StreamModule_present(st_1, w_3), points)), produced);
    const streamsOf = (affected) => {
        if (isEmpty_1(affected) ? true : affected.Equals(producedSet)) {
            return empty();
        }
        else {
            return filter((value) => FSharpSet__Contains(affected, value), StreamModule_all);
        }
    };
    const gaps_1 = map_1((tupledArg_2) => {
        const e_2 = tupledArg_2[1];
        return mkIssue(new IssueKind(0, []), tupledArg_2[0], e_2, 0, undefined, equals_1(e_2, rangeEnd), streamsOf(tupledArg_2[2]));
    }, coalesceBy(union, collect((tuple) => tuple[0], groups)));
    const endsInGap = exists((_arg_3) => _arg_3.IsOngoing, gaps_1);
    const delays_1 = map_1((tupledArg_5) => {
        const _arg_4 = tupledArg_5[2];
        return mkIssue(new IssueKind(1, []), tupledArg_5[0], tupledArg_5[1], _arg_4[1], _arg_4[0], _arg_4[2] && !endsInGap, streamsOf(_arg_4[3]));
    }, coalesceBy((tupledArg_3, tupledArg_4) => [max((x_4, y_4) => (compare(x_4, y_4) | 0), tupledArg_3[0], tupledArg_4[0]), tupledArg_3[1] + tupledArg_4[1], tupledArg_3[2] ? true : tupledArg_4[2], union(tupledArg_3[3], tupledArg_4[3])], collect((tuple_1) => tuple_1[1], groups)));
    const interval_1 = typicalInterval(points);
    return new Report(sensorId, rangeStart, rangeEnd, length(points), interval_1, ThresholdsModule_gap(thresholds, interval_1), ThresholdsModule_delay(thresholds, interval_1), gaps_1, delays_1, tryMax(append(ofArray(toArray(lastPointTime)), ofArray(toArray(lastKnown)))));
}

/**
 * The window cut into the stretches the report found, the healthy ones in between included, in
 * chronological order - what a coverage bar of the window is drawn from. `None` marks a stretch
 * the sensor was reporting normally in. Gaps and delayed intervals never overlap: a gap is a
 * period with no measurement, and only a measurement can be delayed.
 */
export function timeline(report) {
    return segments(report.From, report.To, map_1((i_1) => [i_1.Kind, i_1.From, i_1.To], sortBy((i) => [getTicks(i.From), (i.Kind.tag === 1) ? 1 : 0], append(report.Gaps, report.Delays), {
        Compare: (x, y) => (compareArrays(x, y) | 0),
    })));
}

